import React from 'react';
import { Trans } from 'react-i18next';
import { View } from 'react-native';

import { DriverInfo } from '@components/DriverInfo';
import { LocalisedServerValue } from '@components/LocalisedServerValue';
import { Order } from '@lib/types';
import { Typography } from '@components/Typography';
import { firebase } from '@lib/firebase';
import { makeStyles } from '@providers/Theme';

import { AddressPart } from './AddressPart';
import { UserDetailsRow } from './UserDetailsRow';
import { UserMap } from './UserMap';

type UserDetailsProps = {
  order: Order;
  isPrint?: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    ...theme.mixins.padding(2, 0),
    borderTopColor: theme.palette.grey[300],
    borderTopWidth: 1,
    borderBottomColor: theme.palette.grey[300],
    borderBottomWidth: 1,
  },
  header: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  addressDetails: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
  },
}));

export const UserDetails: React.FC<UserDetailsProps> = ({ order, isPrint }) => {
  const styles = useStyles();
  const isFleet = order?.orderMessage?.data?.fleet;
  const isOD = !isFleet;
  // Fallback to the hardcoded number if remote config fails
  const supportPhoneNumber = firebase.getRemoteConfigString('SUPPORT_NUMBER') || '0225992670';

  return (
    <View style={styles.root}>
      {order?.tripDriverCache && (
        <DriverInfo
          showTracking={true}
          textSize="md"
          driver={order.tripDriverCache}
        />
      )}

      {!isPrint && (
        <Typography
          weight="bold"
          size="md"
          variant="warning"
          style={styles.header}>
          <Trans
            i18nKey="orderDetails.userDetails.callUsNotTheCustomer"
            defaults="Call us not the customer ({{phoneNumber}})"
            values={{ phoneNumber: supportPhoneNumber }}
          />
        </Typography>
      )}

      <UserDetailsRow
        icon="user"
        value={
          <Typography weight="bold">
            {order?.orderMessage?.data?.user?.name || '---'}
          </Typography>
        }
      />

      <UserDetailsRow
        icon="phone"
        value={
          <Typography weight="bold">
            {order?.orderMessage?.data?.user?.phone || '---'}
          </Typography>
        }
      />

      {isOD && order?.orderMessage?.data?.deliveryAddress && (
        <UserDetailsRow
          icon="target"
          value={
            <Typography weight="bold">
              {order?.orderMessage?.data?.deliveryAddress?.address}
            </Typography>
          }
          details={
            <View style={styles.addressDetails}>
              <AddressPart
                label={<Trans i18nKey="address.city" defaults="City" />}
                value={
                  <LocalisedServerValue
                    value={order?.orderMessage?.data?.deliveryAddress?.city?.name}
                  />
                }
              />
              <AddressPart
                label={<Trans i18nKey="address.area" defaults="Area" />}
                value={
                  <LocalisedServerValue
                    value={order?.orderMessage?.data?.deliveryAddress?.area?.name}
                  />
                }
              />
              <AddressPart
                label={<Trans i18nKey="address.zone" defaults="Zone" />}
                value={
                  <LocalisedServerValue
                    value={order?.orderMessage?.data?.deliveryAddress?.zone?.name}
                  />
                }
              />
              <AddressPart
                label={<Trans i18nKey="address.buildingNumber" defaults="Building no." />}
                value={order?.orderMessage?.data?.deliveryAddress?.buildingNumber}
              />
              <AddressPart
                label={<Trans i18nKey="address.apartmentNumber" defaults="Apartment no." />}
                value={order?.orderMessage?.data?.deliveryAddress?.apartmentNumber}
              />
              <AddressPart
                label={<Trans i18nKey="address.floorNumber" defaults="Floor no." />}
                value={order?.orderMessage?.data?.deliveryAddress?.floor}
              />
            </View>
          }
        />
      )}

      {isOD && order?.orderMessage?.data?.deliveryAddress?.company && (
        <UserDetailsRow
          icon="building"
          value={
            <Typography weight="bold">
              <Trans
                i18nKey="orderDetails.userDetails.thisIsACompanyAddress"
                defaults="This is a <bold>company</bold> address"
                // @ts-ignore
                components={{ bold: <Typography size="sm" weight="bold" /> }}
              />
            </Typography>
          }
        />
      )}

      {!isPrint && <UserMap order={order} />}
    </View>
  );
};